import isEmpty from 'lodash/isEmpty';

export const CLOUDFLARE_TURNSTILE = 'CloudflareTurnstile';
export const GOOGLE_RECAPTCHA = 'GoogleReCAPTCHA';

const CLOUDFLARE_SCRIPT_ID = 'swf-cloudflare-turnstile-script';
const GOOGLE_RECAPTCHA_SCRIPT_ID = 'swf-google-recaptcha-script';

const providerSettings = {
    [CLOUDFLARE_TURNSTILE]: {
        className: 'cf-turnstile',
        metaName: 'swf-cloudflare-turnstile-site-key',
        configAttribute: 'data-cloudflare-turnstile-site-key',
        defaultTheme: 'auto',
        themes: ['auto', 'light', 'dark'],
        sizes: ['normal', 'flexible', 'compact'],
        scriptId: CLOUDFLARE_SCRIPT_ID,
        scriptUrl: 'https://challenges.cloudflare.com/turnstile/v0/api.js',
        tokenName: 'cf-turnstile-response',
    },
    [GOOGLE_RECAPTCHA]: {
        className: 'g-recaptcha',
        metaName: 'swf-google-recaptcha-site-key',
        configAttribute: 'data-google-recaptcha-site-key',
        defaultTheme: 'light',
        themes: ['light', 'dark'],
        sizes: ['normal', 'compact'],
        scriptId: GOOGLE_RECAPTCHA_SCRIPT_ID,
        scriptUrl: 'https://www.google.com/recaptcha/api.js',
        tokenName: 'g-recaptcha-response',
    },
};

const normalizeProvider = provider =>
    providerSettings[provider] ? provider : CLOUDFLARE_TURNSTILE;

const normalizeTheme = (theme, settings) =>
    settings.themes.includes(theme) ? theme : settings.defaultTheme;

const normalizeSize = (size, settings) => settings.sizes.includes(size) ? size : 'normal';

const siteKey = (document, settings) =>
    document?.querySelector(`meta[name="${settings.metaName}"]`)?.getAttribute('content') ||
    document?.querySelector('.swf-bot-challenge-config')?.getAttribute(settings.configAttribute) ||
    '';

const cspNonce = document => {
    const script = document?.querySelector('script[nonce]');
    return script?.nonce || script?.getAttribute('nonce') || '';
};

const ensureScript = (document, settings) => {
    if (!document || document.getElementById(settings.scriptId)) {
        return;
    }

    const script = document.createElement('script');
    script.id = settings.scriptId;
    script.async = true;
    script.defer = true;
    script.src = settings.scriptUrl;
    const nonce = cspNonce(document);
    if (!isEmpty(nonce)) {
        script.setAttribute('nonce', nonce);
    }
    (document.head || document.body).appendChild(script);
};

const currentDocument = () => typeof window === 'undefined' ? null : window.document;

export const setupBotChallenges = (root = currentDocument(), document = root) => {
    if (!root) {
        return;
    }

    const challenges = [
        ...(root.matches?.('.swf-bot-challenge') ? [root] : []),
        ...(root.querySelectorAll?.('.swf-bot-challenge') || []),
    ];
    const providers = new Set();

    challenges.forEach(challenge => {
        const provider = normalizeProvider(challenge.getAttribute('data-provider'));
        const settings = providerSettings[provider];
        const providerSiteKey = siteKey(document, settings);

        Object.values(providerSettings).forEach(value => {
            challenge.classList.remove?.(value.className);
        });
        challenge.setAttribute('data-provider', provider);
        challenge.setAttribute('data-theme',
            normalizeTheme(challenge.getAttribute('data-theme'), settings));
        challenge.setAttribute('data-size', normalizeSize(challenge.getAttribute('data-size'), settings));
        challenge.removeAttribute?.('data-sitekey');
        challenge.removeAttribute?.('data-missing-site-key');

        if (isEmpty(providerSiteKey)) {
            challenge.setAttribute('data-missing-site-key', 'true');
            return;
        }

        challenge.classList.add(settings.className);
        challenge.setAttribute('data-sitekey', providerSiteKey);
        providers.add(provider);
    });

    providers.forEach(provider => ensureScript(document, providerSettings[provider]));
};

export const botChallengeTokenValues = (form, document = currentDocument()) =>
    Object.values(providerSettings).reduce((tokens, settings) => {
        const field =
            form?.querySelector?.(`[name="${settings.tokenName}"]`) ||
            document?.querySelector?.(`[name="${settings.tokenName}"]`);

        if (field && !isEmpty(field.value)) {
            tokens[settings.tokenName] = field.value;
        }

        return tokens;
    }, {});
